import { create } from 'zustand';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export type Notification = {
  id: number;
  recipient_id: string;
  task_id: string;
  type: string;
  title: string;
  message: string; // JSON string with workerName, clientName, taskTitle, reason
  is_read: boolean;
  sent_via: string;
  created_at: string;
  read_at?: string;
  task_title?: string;
  task_description?: string;
};

type NotificationStore = {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  lastCheckTime: number;
  
  fetchUnreadNotifications: (userId: string) => Promise<void>;
  markAsRead: (notificationId: number) => Promise<void>;
  markAllAsRead: (userId: string) => Promise<void>;
};

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  lastCheckTime: 0,

  fetchUnreadNotifications: async (userId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${API_BASE}/notifications/unread?userId=${userId}`);
      if (!response.ok) throw new Error('Failed to fetch notifications');
      
      const data = await response.json();
      const currentTime = Date.now();
      
      set({ 
        notifications: data, 
        unreadCount: data.length,
        isLoading: false,
        lastCheckTime: currentTime
      });
    } catch (error) {
      console.error('Error fetching notifications:', error);
      set({ error: 'Failed to fetch notifications', isLoading: false });
    }
  },

  markAsRead: async (notificationId: number) => {
    try {
      const response = await fetch(`${API_BASE}/notifications/${notificationId}/read`, {
        method: 'POST'
      });
      if (!response.ok) throw new Error('Failed to mark notification as read');
      
      // Update local state
      set(state => ({
        notifications: state.notifications.map(n => 
          n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
        ).filter(n => !n.is_read), // Remove read notifications from list
        unreadCount: Math.max(0, state.unreadCount - 1)
      }));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  },

  markAllAsRead: async (userId: string) => {
    try {
      const response = await fetch(`${API_BASE}/notifications/read-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });
      if (!response.ok) throw new Error('Failed to mark all notifications as read');
      
      // Clear all notifications
      set({ notifications: [], unreadCount: 0 });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }
}));
